import type { ChildId, ChildWithProfile, Collection, CollectionId } from '@kidshub/contracts';
import {
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { apiClient } from '../lib/api-client.js';
import { toErrorKey } from '../lib/error-code.js';
import { Button, Icon, Segmented, Switch } from '../ui/kit.js';
import { ARTWORK_KEYS, type ArtworkKey, DEFAULT_ARTWORK_KEY } from './artwork-keys.js';

/**
 * Collections, and who they actually reach.
 *
 * A collection is a curation unit rather than a folder, but the part that is
 * easy to get wrong is not the list. It is that **a collection which is not
 * switched on for a child never reaches their tablet at all.** A parent who has
 * spent an evening building one and cannot find it on the tablet will assume a
 * bug, and they will be reasonable to.
 *
 * So every collection card carries one switch per child, in words as well as
 * in position: "Eflin sees it" in sage, "Eflin does not" in clay. It is one tap,
 * on the card itself, never behind a selection. A new collection is switched on
 * for every child by default, because it is empty and opening it costs nothing:
 * each item inside still has to be approved by name.
 */

/** Felt colours for collections, in order; they repeat past the fifth. */
const COLLECTION_FELTS = [
  'var(--kh-color-felt-niloya)',
  'var(--kh-color-felt-pepee)',
  'var(--kh-color-felt-song)',
  'var(--kh-color-felt-sleep)',
  'var(--kh-color-felt-game)',
] as const;

/** The felt for an item that sits in no collection. */
export const LOOSE_ITEM_FELT = 'var(--kh-color-felt-game)';

/**
 * A collection's felt, from its id.
 *
 * Not from its place in the list: collections can be reordered now, and a
 * collection that changed colour when it moved would look like a different one.
 * The tablet derives its tile colours the same way, for the same reason.
 */
export const collectionFelt = (collectionId: string): string => {
  let sum = 0;
  for (let index = 0; index < collectionId.length; index += 1) {
    sum = (sum + collectionId.charCodeAt(index)) % COLLECTION_FELTS.length;
  }
  return COLLECTION_FELTS[sum] ?? LOOSE_ITEM_FELT;
};

interface CollectionsRailProps {
  collections: Collection[];
  /** How many library items each collection holds. */
  countIn: (collection: Collection) => number;
  familyChildren: ChildWithProfile[];
  onCollectionsChanged: () => void;
  onChildrenChanged: () => void;
}

export const CollectionsRail = ({
  collections,
  countIn,
  familyChildren,
  onCollectionsChanged,
  onChildrenChanged,
}: CollectionsRailProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const headingId = useId();
  const [isCreating, setIsCreating] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  // While sorting, the order on screen leads and the server follows each move.
  const [sortedIds, setSortedIds] = useState<CollectionId[] | null>(null);
  const isSorting = sortedIds !== null;
  const shown = isSorting
    ? sortedIds.flatMap((id) => collections.find((collection) => collection.id === id) ?? [])
    : collections;

  const move = async (collectionId: CollectionId, step: number) => {
    if (sortedIds === null) {
      return;
    }
    const from = sortedIds.indexOf(collectionId);
    const next = sortedIds.filter((id) => id !== collectionId);
    next.splice(from + step, 0, collectionId);
    setSortedIds(next);
    setErrorKey(null);

    try {
      await apiClient.collections.reorder({ collectionIds: next });
    } catch (error) {
      setSortedIds(sortedIds);
      setErrorKey(toErrorKey(error));
    }
  };

  const toggleSorting = () => {
    if (isSorting) {
      setSortedIds(null);
      onCollectionsChanged();
    } else {
      setSortedIds(collections.map((collection) => collection.id));
    }
  };

  const enableFor = (entry: ChildWithProfile, enabledCollectionIds: CollectionId[]) =>
    apiClient.children.updateProfile({
      childId: entry.child.id,
      profile: { enabledCollectionIds },
    });

  const toggleEnabled = async (entry: ChildWithProfile, collectionId: CollectionId) => {
    setErrorKey(null);
    setIsBusy(true);

    const enabled = entry.profile.enabledCollectionIds;
    const next = enabled.includes(collectionId)
      ? enabled.filter((id) => id !== collectionId)
      : [...enabled, collectionId];

    try {
      await enableFor(entry, next);
      onChildrenChanged();
    } catch (error) {
      setErrorKey(toErrorKey(error));
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <section className="lib-colls" aria-labelledby={headingId}>
      <div className="lib-colls-head lib-colls-head-row">
        <div>
          <h2 className="t-h2" id={headingId}>
            {t('library.collections')}
          </h2>
          <p className="t-meta">
            {isSorting ? t('library.collection.sortHint') : t('library.collection.hint')}
          </p>
        </div>
        {collections.length < 2 ? null : (
          <Button variant={isSorting ? 'primary' : 'ghost'} small onClick={toggleSorting}>
            {isSorting ? t('library.collection.sortDone') : t('library.collection.sort')}
          </Button>
        )}
      </div>

      <div className="coll-grid">
        {shown.map((collection, index) => {
          const count = countIn(collection);

          return (
            <div
              key={collection.id}
              className={isSorting ? 'coll is-sorting' : 'coll'}
              style={{ '--c': collectionFelt(collection.id) } as CSSProperties}
            >
              <Link className="coll-sel" to={`/library/collections/${collection.id}`}>
                <b>{collection.title}</b>
                <span className="num">
                  {count === 0 ? t('library.collection.empty') : t('library.itemCount', { count })}
                </span>
              </Link>

              {isSorting ? (
                <div className="lib-coll-move">
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={t('library.collection.moveEarlier', { title: collection.title })}
                    disabled={index === 0}
                    onClick={() => void move(collection.id, -1)}
                  >
                    <Icon name="left" />
                  </button>
                  <span className="num">{index + 1}.</span>
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={t('library.collection.moveLater', { title: collection.title })}
                    disabled={index === shown.length - 1}
                    onClick={() => void move(collection.id, 1)}
                  >
                    <Icon name="right" />
                  </button>
                </div>
              ) : null}

              {isSorting
                ? null
                : familyChildren.map((entry) => {
                    const isOn = entry.profile.enabledCollectionIds.includes(collection.id);
                    const name = entry.child.name;

                    return (
                      <button
                        key={entry.child.id}
                        type="button"
                        className="coll-vis"
                        role="switch"
                        aria-checked={isOn}
                        aria-label={t('library.collection.switchLabel', {
                          collection: collection.title,
                          name,
                        })}
                        disabled={isBusy}
                        onClick={() => void toggleEnabled(entry, collection.id)}
                      >
                        <span>
                          {isOn
                            ? t('library.collection.sees', { name })
                            : t('library.collection.doesNotSee', { name })}
                        </span>
                        <span className="switch" data-on={isOn} aria-hidden="true" />
                      </button>
                    );
                  })}
            </div>
          );
        })}

        {isSorting ? null : (
          <button type="button" className="coll coll-new" onClick={() => setIsCreating(true)}>
            <Icon name="plus" />
            {t('library.newCollection')}
          </button>
        )}
      </div>

      {errorKey === null ? null : (
        <p className="help err" role="alert">
          {t(errorKey)}
        </p>
      )}

      {isCreating ? (
        <NewCollectionSheet
          familyChildren={familyChildren}
          enableFor={enableFor}
          onClose={() => setIsCreating(false)}
          onCreated={(collectionId) => {
            setIsCreating(false);
            void navigate(`/library/collections/${collectionId}`);
          }}
          onCollectionsChanged={onCollectionsChanged}
          onChildrenChanged={onChildrenChanged}
        />
      ) : null}
    </section>
  );
};

interface NewCollectionSheetProps {
  familyChildren: ChildWithProfile[];
  enableFor: (entry: ChildWithProfile, enabledCollectionIds: CollectionId[]) => Promise<unknown>;
  onClose: () => void;
  onCreated: (collectionId: CollectionId) => void;
  onCollectionsChanged: () => void;
  onChildrenChanged: () => void;
}

/**
 * Naming a collection and, in the same step, saying who sees it.
 *
 * Asking here is the cheapest place to stop the forgotten switch: the parent is
 * already thinking about who the collection is for.
 */
const NewCollectionSheet = ({
  familyChildren,
  enableFor,
  onClose,
  onCreated,
  onCollectionsChanged,
  onChildrenChanged,
}: NewCollectionSheetProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const createdId = useRef<CollectionId | null>(null);
  const [title, setTitle] = useState('');
  const [artworkKey, setArtworkKey] = useState<ArtworkKey>(DEFAULT_ARTWORK_KEY);
  const [showTo, setShowTo] = useState<ReadonlySet<ChildId>>(
    () => new Set(familyChildren.map((entry) => entry.child.id)),
  );
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    // Back to whatever opened the sheet when it closes, as a dialog should.
    const opener = document.activeElement;
    inputRef.current?.focus();
    return () => {
      if (opener instanceof HTMLElement) {
        opener.focus();
      }
    };
  }, []);

  const setShown = (childId: ChildId, isShown: boolean) => {
    const next = new Set(showTo);
    if (isShown) {
      next.add(childId);
    } else {
      next.delete(childId);
    }
    setShowTo(next);
  };

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setErrorKey(null);
    setIsBusy(true);

    try {
      // A retry after a failed switch must not make a second collection.
      const collectionId =
        createdId.current ?? (await apiClient.collections.create({ title, artworkKey })).id;
      createdId.current = collectionId;
      // One child at a time: each update replaces that child's whole list.
      for (const entry of familyChildren.filter((candidate) => showTo.has(candidate.child.id))) {
        if (!entry.profile.enabledCollectionIds.includes(collectionId)) {
          await enableFor(entry, [...entry.profile.enabledCollectionIds, collectionId]);
        }
      }
      onCreated(collectionId);
    } catch (error) {
      setErrorKey(toErrorKey(error));
      setIsBusy(false);
    } finally {
      // Even a half-finished create leaves a collection behind; show it.
      onCollectionsChanged();
      onChildrenChanged();
    }
  };

  const closeOnEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  return (
    <>
      <div className="scrim open" aria-hidden="true" onClick={onClose} />
      <form
        className="sheet open"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={(event) => void create(event)}
        onKeyDown={closeOnEscape}
      >
        <div className="sheet-grip" aria-hidden="true" />
        <h2 className="t-h2" id={titleId}>
          {t('library.newCollection')}
        </h2>

        <div className="sheet-body">
          <div className="field">
            <label htmlFor={inputId}>{t('library.collectionTitle')}</label>
            <input
              ref={inputRef}
              id={inputId}
              className="input"
              type="text"
              required
              maxLength={40}
              placeholder={t('library.collection.titlePlaceholder')}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          <div className="field">
            <span className="t-label">{t('library.collection.artwork')}</span>
            <Segmented
              label={t('library.collection.artwork')}
              value={artworkKey}
              options={ARTWORK_KEYS.map((key) => ({ value: key, label: t(`artwork.${key}`) }))}
              onChange={setArtworkKey}
            />
          </div>

          {familyChildren.length === 0 ? null : (
            <div>
              {familyChildren.map((entry) => {
                const label = t('library.collection.showTo', { name: entry.child.name });
                return (
                  <div key={entry.child.id} className="switch-row">
                    <span>{label}</span>
                    <Switch
                      checked={showTo.has(entry.child.id)}
                      onChange={(isShown) => setShown(entry.child.id, isShown)}
                      label={label}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {errorKey === null ? null : (
          <p className="help err" role="alert">
            {t(errorKey)}
          </p>
        )}

        <div className="sheet-actions">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={isBusy}>
            {t('library.collection.create')}
          </Button>
        </div>
      </form>
    </>
  );
};
